import React from "react";

/**
 * GooglePlayBadge — the official-style "Get it on Google Play" badge.
 *
 * Uses an inline SVG (Google Play triangle in brand colours) + typography
 * that mirrors Google's brand guidelines. Rendered as an <a target="_blank">
 * that opens the HisaabPro Play Store listing.
 *
 * Props:
 *   size:     "sm" | "md" | "lg"   (default "md")
 *   variant:  "dark" | "light"     (default "dark" — black pill, white text)
 *   className: extra tailwind classes
 *   testId:    data-testid override
 */
export const PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=com.myhisaabpro.app";

export default function GooglePlayBadge({
  size = "md",
  variant = "dark",
  className = "",
  testId = "google-play-badge",
}) {
  const sizes = {
    sm: { pill: "h-10 px-3 gap-2",   icon: 18, top: "text-[9px]",  bot: "text-[13px]" },
    md: { pill: "h-12 px-4 gap-2.5", icon: 22, top: "text-[10px]", bot: "text-[16px]" },
    lg: { pill: "h-14 px-5 gap-3",   icon: 26, top: "text-[11px]", bot: "text-[18px]" },
  };
  const s = sizes[size] || sizes.md;

  const tone =
    variant === "light"
      ? "bg-white text-slate-900 border-slate-300 hover:bg-slate-50"
      : "bg-black text-white border-black hover:bg-slate-900";

  return (
    <a
      href={PLAY_STORE_URL}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Get HisaabPro on Google Play"
      data-testid={testId}
      className={`inline-flex items-center rounded-lg border ${tone} ${s.pill}
        font-sans transition select-none no-underline shrink-0
        focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 ${className}`}
    >
      {/* Google Play triangle */}
      <svg
        width={s.icon}
        height={s.icon}
        viewBox="0 0 512 512"
        aria-hidden="true"
        className="shrink-0"
      >
        <defs>
          <linearGradient id="gpb-a" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#00A0FF" />
            <stop offset="1" stopColor="#00E2FF" />
          </linearGradient>
          <linearGradient id="gpb-b" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#FFCE00" />
            <stop offset="1" stopColor="#FFBD00" />
          </linearGradient>
          <linearGradient id="gpb-c" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#FF3A44" />
            <stop offset="1" stopColor="#C31162" />
          </linearGradient>
          <linearGradient id="gpb-d" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#32A071" />
            <stop offset="1" stopColor="#2DA771" />
          </linearGradient>
        </defs>
        <path d="M79 27 L340 256 L79 485 A32 32 0 0 1 64 460 V52 A32 32 0 0 1 79 27 Z" fill="url(#gpb-a)" />
        <path d="M340 256 L410 186 L448 218 A32 32 0 0 1 448 294 L410 326 Z" fill="url(#gpb-b)" />
        <path d="M340 256 L410 326 L110 496 A32 32 0 0 1 79 485 Z" fill="url(#gpb-d)" />
        <path d="M340 256 L110 16 A32 32 0 0 1 79 27 L410 186 Z" fill="url(#gpb-c)" />
      </svg>
      <span className="flex flex-col items-start leading-none">
        <span className={`${s.top} font-medium opacity-80 tracking-wider uppercase`}>Get it on</span>
        <span className={`${s.bot} font-semibold tracking-tight -mt-0.5`}>Google Play</span>
      </span>
    </a>
  );
}
